import React, { useCallback, useEffect, useState } from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import ProviderSettingsPanel from '@devnw/cyber-iam/identity/ProviderSettingsPanel/ProviderSettingsPanel'
import type {
  ProviderField,
} from '@devnw/cyber-iam/iam/types'
import { PamLoading, PamError } from '../components/PamStateComponents'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import type {
  AdminPutSecretsProviderReq,
  AdminSecretsProviderResp,
} from '../../../gen/client/react/client'

/**
 * TenantSecretsProvider — per-tenant secrets-provider configuration admin
 * page (Task 15, tenant-secret-mail-providers plan, Phase 4).
 *
 * OIDCPAM Gate-6 Phase 6c: migrated off MUI onto
 * @devnw/cyber-iam/identity/ProviderSettingsPanel — the SAME component
 * TenantMailProvider.tsx uses, supplied with the SECRETS field schema
 * below (byte-identical page shape to TenantMailProvider.tsx modulo the
 * field schema and endpoints, per the task brief). The component owns the
 * write-only credential input, the env-locked read-only projection, and the
 * delete two-phase confirm. This page owns every API call
 * (adminGetSecretsProvider / adminPutSecretsProvider /
 * adminDeleteSecretsProvider), the tenant/routing/404-vs-error shell, and
 * the wire-mapping below.
 *
 * WIRE MAPPING: the generated client's decodeJSON()/fromWire() already
 * snake_case -> camelCase converts the response, so AdminSecretsProviderResp
 * is ALREADY camelCase at this boundary (backend, address, kvMount,
 * namespace, region, endpoint, project, vaultUrl, credentialSet, envLocked,
 * tenantId, createdAt, updatedAt). The `fields` schema below names each
 * editable field to MATCH those response keys 1:1 — so `toSecretsConfig` is
 * a pure passthrough with zero snake<->camel translation, and `handleSave`
 * reads `values.<sameName>` straight into AdminPutSecretsProviderReq with no
 * renaming either (see MtlsCAs.tsx's latent-bug note for the class of bug
 * this avoids).
 *
 * Env-lock: admin_secrets_provider_server.go's AdminGetSecretsProvider
 * ALWAYS returns 200 for the top-level/default tenant (config.TenantID) — a
 * live projection of the env-configured provider with env_locked:true — and
 * its AdminPutSecretsProvider/AdminDeleteSecretsProvider fail closed with
 * 400 for that tenant regardless of caller permissions. ProviderSettingsPanel
 * renders that response as a read-only field list with no editable
 * credential input and disabled Save/Delete, so the UI never even offers a
 * mutation the server would reject. Every other tenant's GET returns 404
 * until first configured; that is NOT an error — `config` stays `null` and
 * the component renders its own "not configured yet" copy and a
 * ready-to-fill form.
 */

const BACKEND_OPTIONS = [
  { value: 'local', label: 'Local (dev/test)' },
  { value: 'vault', label: 'HashiCorp Vault' },
  { value: 'bao', label: 'OpenBao' },
  { value: 'aws', label: 'AWS Secrets Manager' },
  { value: 'gcp', label: 'GCP Secret Manager' },
  { value: 'azure', label: 'Azure Key Vault' },
]

// All connection fields are shown regardless of the selected backend
// (simpler and more maintainable than a backend-conditional field set — see
// task brief); each label names which backend(s) actually consume it.
const secretsProviderFields: ProviderField[] = [
  {
    name: 'backend',
    label: 'Backend',
    type: 'select',
    options: BACKEND_OPTIONS,
    required: true,
  },
  { name: 'address', label: 'Address (Vault / Bao)', type: 'text' },
  { name: 'kvMount', label: 'KV Mount (Vault / Bao)', type: 'text' },
  { name: 'namespace', label: 'Namespace (Bao)', type: 'text' },
  { name: 'region', label: 'Region (AWS)', type: 'text' },
  { name: 'endpoint', label: 'Endpoint (AWS)', type: 'text' },
  { name: 'project', label: 'Project (GCP)', type: 'text' },
  { name: 'vaultUrl', label: 'Vault URL (Azure)', type: 'text' },
  { name: 'credential', label: 'Credential', type: 'password' },
]

/**
 * Extracts a human-readable message from a rejection. Duck-types on a string
 * `message` property rather than requiring `instanceof Error` — the real
 * generated-client HTTPError IS an Error subclass so this still reads its
 * resolved message, but the check also works for any plain error-shaped
 * object, matching httpStatus's "read fields directly for reliability" below.
 */
function errMessage(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'message' in e) {
    const m = (e as { message: unknown }).message
    if (typeof m === 'string' && m) return m
  }
  return 'Request failed'
}

/**
 * Extracts the HTTP status from a generated-client HTTPError WITHOUT an
 * `instanceof` check, matching GovernanceRoleRegistry.tsx's
 * friendlyApiError precedent ("read .status directly for reliability").
 * Works for both the real HTTPError class and a plain mocked error object.
 */
function httpStatus(e: unknown): number {
  if (e !== null && typeof e === 'object' && 'status' in e) {
    const s = (e as { status: unknown }).status
    if (typeof s === 'number') return s
  }
  return 0
}

/** AdminSecretsProviderResp -> ProviderSettingsPanel's `config` prop — a
 *  pure field-name passthrough (see the file header's wire-mapping note).
 *  The write-only `credential` is deliberately absent: the response never
 *  carries it, and the component never seeds a password field from `config`
 *  regardless. */
function toSecretsConfig(
  resp: AdminSecretsProviderResp
): Record<string, unknown> {
  return {
    backend: resp.backend,
    address: resp.address,
    kvMount: resp.kvMount,
    namespace: resp.namespace,
    region: resp.region,
    endpoint: resp.endpoint,
    project: resp.project,
    vaultUrl: resp.vaultUrl,
  }
}

export const TenantSecretsProvider = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [config, setConfig] = useState<AdminSecretsProviderResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!tenant) return undefined
    let cancelled = false
    setLoading(true)
    setError(undefined)
    client
      .adminGetSecretsProvider({ tenantId: tenant })
      .then((resp) => {
        if (cancelled) return
        setConfig(resp)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        if (httpStatus(e) === 404) {
          // Not an error — this tenant simply has no stored config yet.
          setConfig(null)
        } else {
          setError(errMessage(e))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, tenant])

  const handleSave = useCallback(
    async (values: Record<string, unknown>): Promise<void> => {
      const req: AdminPutSecretsProviderReq = {
        tenantId: tenant,
        backend: String(values.backend || 'local'),
        address: String(values.address ?? ''),
        kvMount: String(values.kvMount ?? ''),
        namespace: String(values.namespace ?? ''),
        region: String(values.region ?? ''),
        endpoint: String(values.endpoint ?? ''),
        project: String(values.project ?? ''),
        vaultUrl: String(values.vaultUrl ?? ''),
        // Write-only + leave-blank-to-keep-unchanged: only send a
        // credential when the admin actually typed one (mirrors
        // Connectors.tsx's client_secret handling). ProviderSettingsPanel
        // already omits this key from `values` entirely when left blank.
        credential: values.credential
          ? String(values.credential)
          : undefined,
      }
      const resp = await client.adminPutSecretsProvider(req)
      setConfig(resp)
    },
    [client, tenant]
  )

  const handleDelete = useCallback(async (): Promise<void> => {
    await client.adminDeleteSecretsProvider({ tenantId: tenant })
    setConfig(null)
  }, [client, tenant])

  // The initial GET's loading/error states are handled as page-level early
  // returns (StepUpPolicy.tsx precedent) rather than passed into
  // ProviderSettingsPanel's own `loading`/`error` props: the component's
  // `config === null` branch unconditionally renders its own "not
  // configured yet" copy, which would otherwise render ALONGSIDE a genuine
  // (non-404) fetch-error banner — `config` stays `null` in both the
  // not-yet-configured AND the real-error case in this page's state model.
  // Gating here keeps "not configured" and "load failed" mutually exclusive.
  if (loading) return <PamLoading />
  if (error) return <PamError message={error} />

  return (
    <ProviderSettingsPanel
      testID="secrets-provider"
      title="Secrets Provider"
      description="Secret-storage backend configuration."
      tenant={tenant}
      fields={secretsProviderFields}
      config={config ? toSecretsConfig(config) : null}
      credentialSet={config?.credentialSet ?? false}
      envLocked={config?.envLocked ?? false}
      onSave={handleSave}
      onDelete={config ? handleDelete : undefined}
    />
  )
}
